/**
 * Trading API Service
 * Handles communication with backend trading endpoints
 */

const TRADING_API_URL = process.env.REACT_APP_TRADING_API_URL || "http://localhost:5000";

/**
 * Prefetch transaction data (bonding curve, global account, fee config)
 * This is called when user shows intent to buy (hover, typing amount)
 * @param {string} mint - Token mint address
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<Object>} Prefetched data or error
 */
export const prefetchBuyData = async (mint, walletAddress) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/trade/prefetch-buy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mint,
        walletAddress,
      }),
    });

    if (!response.ok) {
      // Silently fail - prefetch errors shouldn't block the UI
      return { error: "Prefetch failed" };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // Silently fail - prefetch errors shouldn't block the UI
    console.debug("Prefetch error (non-blocking):", error.message);
    return { error: error.message };
  }
};

/**
 * Execute a buy order
 * @param {string} mint - Token mint address
 * @param {number} amountInSol - Amount in SOL to spend
 * @param {string} walletAddress - Wallet public key
 * @param {Object} cachedData - Optional prefetched data to speed up transaction building
 * @returns {Promise<Object>} Transaction object or error
 */
export const executeBuy = async (mint, amountInSol, walletAddress, slippagePercent = 20.0, priorityFeeSol = 0.0001, tipSol = 0.0001, cachedData = null) => {
  const startTime = performance.now();
  try {
    const response = await fetch(`${TRADING_API_URL}/api/trade/buy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mint,
        amountInSol,
        walletAddress,
        slippagePercent,
        priorityFeeSol,
        tipSol,
        cachedData, // Pass prefetched data if available
      }),
    });

    const endTime = performance.now();
    const duration = endTime - startTime;

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      const errorMsg = error.error || `HTTP error! status: ${response.status}`;
      console.error("Error executing buy:", errorMsg);
      return { error: errorMsg };
    }

    const data = await response.json();
    console.log(`[API] executeBuy completed in ${duration.toFixed(2)}ms`, { status: response.status, hasTransaction: !!(data.transaction || data.instructions) });
    return data;
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    console.error(`[API] executeBuy failed after ${duration.toFixed(2)}ms:`, error);
    return { error: error.message };
  }
};

/**
 * Execute a sell order
 * @param {string} mint - Token mint address
 * @param {number} amountToken - Amount of tokens to sell
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<Object>} Transaction object or error
 */
export const executeSell = async (mint, amountToken, walletAddress, slippagePercent = 20.0, priorityFeeSol = 0.0001, tipSol = 0.0001) => {
  const startTime = performance.now();
  try {
    const response = await fetch(`${TRADING_API_URL}/api/trade/sell`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mint,
        amountToken,
        walletAddress,
        slippagePercent,
        priorityFeeSol,
        tipSol,
      }),
    });

    const endTime = performance.now();
    const duration = endTime - startTime;

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      const errorMsg = error.error || `HTTP error! status: ${response.status}`;
      console.error(`[API] executeSell failed after ${duration.toFixed(2)}ms:`, errorMsg);
      return { error: errorMsg };
    }

    const data = await response.json();
    console.log(`[API] executeSell completed in ${duration.toFixed(2)}ms`, { status: response.status, hasTransaction: !!data.transaction });
    return data;
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    console.error(`[API] executeSell failed after ${duration.toFixed(2)}ms:`, error);
    return { error: error.message };
  }
};

/**
 * Get portfolio for a wallet
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<Object>} Portfolio data
 */
export const getPortfolio = async (walletAddress) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/portfolio?walletAddress=${walletAddress}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting portfolio:", error);
    return {
      open_positions: [],
      closed_positions: [],
      fills: [],
    };
  }
};

/**
 * Get open positions for a wallet
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<Array>} Array of open positions
 */
export const getOpenPositions = async (walletAddress) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/positions?walletAddress=${walletAddress}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.positions || [];
  } catch (error) {
    console.error("Error getting positions:", error);
    return [];
  }
};

/**
 * Import private key for backend signing
 * @param {string} walletAddress - Wallet public key
 * @param {string} privateKey - Base58 encoded private key
 * @returns {Promise<boolean>} Success status
 */
export const importPrivateKey = async (walletAddress, privateKey) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/wallet/import-key`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        walletAddress,
        privateKey,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error("Error importing private key:", error);
    return false;
  }
};

/**
 * Remove private key for a wallet
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<boolean>} Success status
 */
export const removePrivateKey = async (walletAddress) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/wallet/remove-key`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        walletAddress,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error("Error removing private key:", error);
    return false;
  }
};

/**
 * Check if wallet has a stored private key
 * @param {string} walletAddress - Wallet public key
 * @returns {Promise<boolean>} True if private key is stored
 */
export const hasPrivateKey = async (walletAddress) => {
  try {
    const response = await fetch(`${TRADING_API_URL}/api/wallet/has-key?address=${walletAddress}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.hasKey === true;
  } catch (error) {
    console.error("Error checking private key:", error);
    return false;
  }
};

/**
 * Get SPL token balance for a wallet and mint
 * @param {string} walletAddress - Wallet public key
 * @param {string} mint - Token mint address
 * @returns {Promise<Object>} Token balance data
 */
export const getTokenBalance = async (walletAddress, mint) => {
  // Dynamic import to avoid circular dependencies
  const loggerModule = await import('../utils/logger');
  const log = loggerModule.default;
  
  try {
    const url = `${TRADING_API_URL}/api/wallet/token-balance?address=${walletAddress}&mint=${mint}`;
    log.debug('API', `Fetching token balance: ${walletAddress.substring(0, 8)}... / ${mint.substring(0, 8)}...`);
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: AbortSignal.timeout(30000) // 30 second timeout
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      const errorMsg = errorData.error || `HTTP error! status: ${response.status}`;
      log.error('API', `Token balance HTTP error ${response.status}`, { error: errorMsg, wallet: walletAddress.substring(0, 8), mint: mint.substring(0, 8) });
      throw new Error(errorMsg);
    }

    const data = await response.json();
    
    // Log if there's an error in the response
    if (data.error) {
      log.error('API', `Token balance backend error`, { error: data.error, wallet: walletAddress.substring(0, 8), mint: mint.substring(0, 8) });
    } else {
      log.debug('API', `Token balance success`, { balance: data.balanceUi, decimals: data.decimals, wallet: walletAddress.substring(0, 8), mint: mint.substring(0, 8) });
    }
    
    return {
      balance: data.balance || "0",
      balanceUi: data.balanceUi || 0,
      decimals: data.decimals || 6,
      hasPosition: data.hasPosition || false,
      error: data.error || null  // Include error if present
    };
  } catch (error) {
    log.error('API', `Token balance exception`, { error: error.message, wallet: walletAddress.substring(0, 8), mint: mint.substring(0, 8) });
    return {
      balance: "0",
      balanceUi: 0,
      decimals: 6,
      hasPosition: false,
      error: error.message
    };
  }
};
